/**
 * Client for the feat-053d Server Scorecard endpoints (routes/server_scorecard.py).
 *
 * Contracts:
 *  - Spec:    worktrees/feat-053d-server-scorecard/spec.md
 *  - ADR 0001 (decisions/0001-server-scorecard.md) — D1 grid aggregate, D2 real
 *    columns (Score/XP/Shifts/Streak), D3 Challenges placeholder, A1 Badges
 *    placeholder, D6 detail Avg-Upsells (real) + Avg-Tip-% (placeholder).
 *
 * THE HONESTY CRUX (ADR §1 + A1): the REAL columns (Score, Total XP, Total
 * Shifts, Current Streak, Avg. Upsells) are real backend sources and render
 * CLEAN. Challenges (n/20), Badges (n/15) and Avg. Tip % are seeded-plausible +
 * labeled — each carries `*_placeholder: true` + `*_data_source`, and the UI
 * attaches the Slate `preview-*` EstimatedMarker (reused from feat-053b) so a
 * manager never reads a seeded value as measured. Avg-Tip-% is NEVER wired to
 * `server.tip_rate` (DECISIONS §4).
 *
 * The frontend NEVER recomputes a metric (feedback_metrics_single_source): it
 * renders server values + server-formatted `*_display` strings verbatim and
 * reads the server-stamped flags.
 *
 * SCORE TIER (design callout #2): the Score cell renders a tier pill
 * ("812 · Excellent" + emerald/indigo/amber/red). `ShiftStats.score` is
 * formula/multiplier-driven (tenant-configurable), so the tier label + color
 * band are BACKEND-owned (`score_tier` / `score_band`) — the frontend never
 * hardcodes scale-dependent thresholds. When the backend omits them, the pill
 * gracefully degrades to a neutral score chip (no fabricated tier).
 *
 * Fake mode (`config.environment === 'fake'`, npm run start:fake): returns a
 * deterministic payload in the same shape (matches the design mockup) so both
 * screens render with no backend. The contract is the real contract — feat-053e
 * swaps the seeded challenges/badges/tip for real sources with NO frontend
 * reshape (the placeholder flags only flip).
 */
import { config } from '../config/environment';
import { authHeader } from './authToken';
import { authedApiFetch } from './tokenRefresh';
import type { GroupHeader } from './scorecardGroups';

// ---------- Domain types (mirror ADR D1 / backend response models) ----------

/** Color key for the Score tier pill (backend-owned — see ScoreTierPill). */
export type ScoreBand = 'exceptional' | 'excellent' | 'good' | 'developing';

/**
 * A flagged metric column definition (ADR D-7 `metric_columns`). Drives the
 * dynamic Server Scorecard headers — ≤7 (ADR 0002 U1, raised 4→7 by feat-296), ordered by the tenant's
 * `display_order`.
 */
export interface MetricColumn {
  key: string; // tenant metric key (matches keys in row.metric_totals)
  label: string; // human-readable header
}

/**
 * Optional drink-promotion column (ADR 0002 U2). Independent of the ≤7 metric
 * cap. Always present in the response; `enabled` gates whether the FE renders
 * it. When enabled the header is `label` and each row's value is `drink_total`.
 */
export interface DrinkColumn {
  enabled: boolean;
  label: string | null; // may be null when the tenant hasn't opted drink in (enabled=false)
}

export interface ServerScorecardRow {
  server_id: string;
  name: string;
  initials: string;
  location_id: string;
  location_name: string;
  // feat-234: the POOLED VISIT-WEIGHTED score over the window —
  // `multiplier × round(Σ attempts ÷ Σ visits × 100)`. Numeric, for sorting;
  // render `score_display` below instead, which is the same figure preformatted.
  // null → "—" (no in-window shifts).
  //
  // STRUCK: this comment used to read "Score = avg(shift.score) over the window
  // — the persisted canonical BespokeScore (a small integer, NOT a 0–100
  // percent)." Both halves went false with feat-234 — it is no longer an average
  // of per-shift scores, and at multiplier 1 it IS rendered as a percent. That
  // is the definition this feature exists to remove, and it survived one line
  // above the rewritten `score_display` comment because the original sweep
  // stopped at the line it was editing.
  score: number | null;
  // feat-234: the pooled visit-weighted score, PREFORMATTED by the backend —
  // "123%" | "—", or a bare "246" for a tenant whose score_multiplier isn't 1
  // (a score is only honestly a percentage at multiplier 1). Rendered VERBATIM;
  // React never appends the suffix and never divides.
  score_display: string;
  // score_tier / score_band are carried by the backend but OPTIONAL-TO-RENDER
  // (human decision O1): the scorecard renders a plain NEUTRAL integer and does
  // NOT paint a tier word or color band. The score has NO ceiling — a server
  // attempting more than one add-on per customer scores past 100 routinely — so
  // feeding it to the old 0–100% thresholds would falsely paint everyone red
  // (feat-084 trap). (The former "~0–200" range here was never a bound.)
  score_tier?: string | null;
  score_band?: ScoreBand | string | null;
  // SUM across in-window shifts (AC#4 — not avg, not per-shift).
  // null = server has 0 in-window shifts ("—"); a real 0 = had shifts, no hits.
  greetings: number | null;
  // feat-093 Slice B: SUM of per-shift `score_breakdown.visit_count` over
  // in-window shifts (greeting-anchored visit count, feat-092). Optional because
  // pre-recalc `v:2` breakdowns omit it; the "Visits" cell reads
  // `visit_count ?? greetings`. Backend is the single source — React never
  // recomputes it. Same null semantics as `greetings` (null = 0 in-window shifts).
  visit_count?: number | null;
  // SUM per flagged metric key over in-window shifts. A present `0` is a real
  // zero (had shifts, no hits → "0"); null/absent = 0 in-window shifts ("—").
  metric_totals: Record<string, number | null>;
  // SUM of drink-promotion hits over in-window shifts (ADR 0002 U2). Always
  // present (backend computes it regardless of drink_column.enabled); the FE
  // only renders it when `drink_column.enabled`. Same null semantics as a metric
  // column: null = 0 in-window shifts ("—"); real 0 = had shifts, no drink hits.
  drink_total: number | null;
  // feat-097 Part B (ADR 0001 Decision 1): per-visit PERCENTAGE per flagged
  // metric key, computed ONCE in the backend with a present-gated denominator
  // (NOT count/visits in React — that would be wrong). Parallel to
  // `metric_totals`; the Scorecard's #/% toggle picks totals vs percents and
  // renders this VERBATIM. Optional so the page still renders against a backend
  // that predates the field (the toggle hides until percents are present). Same
  // null semantics: null = 0 in-window shifts ("—"); real 0 = had shifts, 0%.
  metric_percents?: Record<string, number | null>;
  // feat-097 Part B: per-visit PERCENTAGE for the drink column (parallel to
  // `drink_total`). Same verbatim + null semantics as `metric_percents`.
  drink_percent?: number | null;
}

export interface ServerScorecardResponse {
  // ≤7 flagged metric column definitions, ordered by display_order (drives headers).
  metric_columns: MetricColumn[];
  // Optional drink column (ADR 0002 U2) — render only when `enabled`.
  drink_column?: DrinkColumn;
  rows: ServerScorecardRow[];
  score_windowing?: string; // "windowed"
  // feat-234 — the TENANT's score multiplier. Required on the backend model
  // (routes/server_scorecard.py:190, no default) and required here.
  //
  // It exists so the static tooltip/legend copy can BRANCH. The score is
  // `multiplier × round(Σ attempts ÷ Σ visits × 100)`, so "the score is attempts
  // ÷ visits" is true only at multiplier 1. Five of seven live tenants run at 2,
  // where that sentence contradicts the number it explains. Branching on a number
  // is not arithmetic — no rendered value is derived from it.
  //
  // Do NOT infer the multiplier by sniffing `score_display` for a "%".
  score_multiplier: number;
  // feat-234 — the score at exactly one attempt per visit, PREFORMATTED by the
  // same backend helper as `score_display`: "100%" at m=1, "200" at m=2, "150" at
  // m=1.5. Rendered VERBATIM as the reader's scale anchor, which is what makes a
  // bare score recoverable: `score ÷ anchor` = attempts per customer.
  //
  // NEVER derive this as `100 * score_multiplier` in React. That is score
  // arithmetic on the client (feedback_metrics_single_source, and the spec's
  // "React performs zero arithmetic on scores"), and it breaks on its own terms —
  // at multiplier 1.5 it evaluates to 150.0 and prints a trailing ".0" into the
  // middle of a sentence.
  score_scale_anchor_display: string;
  // feat-317 (ADR §6.2): one leaderboard grid per effective role, in display
  // order. Optional: an old backend omits it (render via groupsOf()).
  groups?: ServerScorecardGroup[];
}

/** feat-317 §6.2 — G4 header + exactly today's leaderboard fields, scoped to one role. */
export interface ServerScorecardGroup extends GroupHeader {
  metric_columns: MetricColumn[];
  drink_column?: DrinkColumn;
  rows: ServerScorecardRow[];
  score_windowing?: string;
  score_multiplier: number;
  score_scale_anchor_display: string;
}

export interface ServerDetailResponse {
  server_id: string;
  name: string;
  initials: string;
  location_id: string;
  location_name: string;
  // ── all-time stat cards (same sources as the grid) ──
  score: number | null;
  score_display: string;
  // feat-234 — same meaning and same source as ServerScorecardResponse's (see
  // the comment there). This payload's `score_display` carries the same
  // conditional `%`, so any copy explaining it needs the same branch.
  // Backend: routes/server_scorecard.py:228 @ 171c725.
  score_multiplier: number;
  // `score_scale_anchor_display` is DELIBERATELY not typed here, and the reason
  // is "nothing on this page consumes it" — NOT "the backend doesn't send it."
  // The backend does send it (or is about to); this type is simply a SUBSET of
  // the payload, which is harmless: extra JSON properties are ignored at
  // runtime, and a field nothing reads is just a second place to keep in sync.
  //
  // The copy that quotes the scale anchor — the aggregate Score tooltip and the
  // legend — lives ONLY on the Scorecard grid, which reads
  // ServerScorecardResponse. If the detail page ever grows copy that needs an
  // anchor, add the field here then, in the same change as the copy.
  //
  // STRUCK, do not act on: an earlier version of this comment said the field was
  // "pending" on the backend. Recording the strike rather than deleting it,
  // because that phrasing was itself the recurring failure of this feature — it
  // stated a fact about another repo's working tree, which goes false the moment
  // that repo commits, on the surface a future reader trusts most.
  score_tier?: string | null;
  score_band?: ScoreBand | string | null;
  total_xp: number;
  total_shifts: number;
  current_streak: number;
  challenges_completed: number;
  challenges_total: number;
  challenges_placeholder: boolean;
  challenges_data_source: string;
  badges_earned: number;
  badges_total: number;
  badges_placeholder: boolean;
  badges_data_source: string;
  // ── performance metrics ──
  avg_upsells: number | null; // REAL (scorecard.py metric_counts)
  avg_upsells_display: string; // "8.4" | "—"
  avg_tip_pct: number; // PLACEHOLDER — NEVER server.tip_rate
  avg_tip_pct_display: string; // "22%"
  avg_tip_pct_placeholder: boolean; // always true this phase
  avg_tip_pct_data_source: string; // "seeded_tip"
  placeholder: boolean;
  data_source: string;
  xp_windowing?: string;
  shifts_windowing?: string;
  score_windowing?: string;
  // feat-317 (ADR §6.6): per-role window score. The web does not render
  // `score` on this page, so nothing consumes this yet; typed for completeness.
  groups?: ServerDetailScoreGroup[];
}

/** feat-317 §6.6 — per-role window score for the server detail endpoint. */
export interface ServerDetailScoreGroup extends GroupHeader {
  score: number | null;
  score_display: string;
  score_multiplier: number;
  score_scale_anchor_display: string;
  score_tier?: string | null;
  score_band?: ScoreBand | string | null;
}

// ---------- HTTP plumbing (mirrors trainingProgressService) ----------

function backendUrl(path: string): string {
  const protocol =
    config.environment === 'fake' || config.environment === 'local' ? 'http' : 'https';
  return `${protocol}://${config.restaurantBackendHost}${path}`;
}

/** Raised when the tenant/server is unreachable (fail-closed 404 — no leak). */
export class ServerScorecardForbiddenError extends Error {
  constructor() {
    super('Server scorecard is not available for this tenant');
    this.name = 'ServerScorecardForbiddenError';
  }
}

async function api<T>(path: string): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...authHeader(),
  };
  const resp = await authedApiFetch(backendUrl(path), { method: 'GET', headers });
  if (resp.status === 404 || resp.status === 403) {
    // Fail-closed tenant scoping (ADR D7) — treat as "not available".
    throw new ServerScorecardForbiddenError();
  }
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`API ${path} ${resp.status}: ${text}`);
  }
  return resp.json();
}

const isFake = (): boolean => config.environment === 'fake';

// ---------- Fake-mode deterministic data (matches the design mockup) ----------
// Scorecard rows (feat-088, D-7): Score is a plain integer, plus Greetings +
// per-metric SUMS. Includes the two distinct null cases the mockup enumerates —
// a real 0 (had shifts, no hits) and an em-dash (0 in-window shifts).
//
// NOTE: FakeServer / FAKE_SERVERS / fakeDetail below are LEFT UNTOUCHED — the
// Server Detail (Performance History) page consumes them and is out of feat-088
// scope (D4 / F4: no changes).

/**
 * feat-234 — TWO fake tenant shapes, because the multiplier is what decides both
 * the `%` suffix and which explanatory copy branch renders, and a fixture that
 * only ever models one of them hides half the app.
 *
 * `fake`    — multiplier 1. Ladle & Leaf, the pilot this feature serves. Scores
 *             carry `%`; the copy states the precise ratio. This is the MINORITY
 *             shape: only 2 of 7 live tenants run at multiplier 1.
 * `fake-m2` — multiplier 2. Bespoke, JouJou, Oren's Hummus, Sajj, The Grove SF —
 *             the MAJORITY. Scores are bare integers; the copy drops the ratio
 *             claim and quotes the scale anchor instead.
 *
 * Reach the second one in a browser with `?tenant=fake-m2` on the Scorecard URL
 * (fake mode only — see ServerScorecard.tsx). The multiplier-1 fixture is
 * deliberately unchanged so existing tests and screenshots stay valid.
 *
 * multiplier and anchor are ONE pair: the anchor is the score at exactly one
 * attempt per visit, so `1 → "100%"`, `2 → "200"`, `1.5 → "150"`. A mismatched
 * pair is a self-contradictory payload, so they are declared together.
 */
export const FAKE_M2_TENANT_ID = 'fake-m2';

/**
 * feat-317 (ADR §7.6) — a TWO-ROLE fake tenant. Every scorecard endpoint
 * returns two `groups` with different metric columns, and the second role
 * ("UCSF / Haas") has no drink column (D9). The top-level fields mirror
 * `groups[0]` (ADR §6.1 T0), exactly as the real backend does. Reach it with
 * `?tenant=fake-mr` wherever `?tenant=fake-m2` works today.
 */
export const FAKE_MR_TENANT_ID = 'fake-mr';

interface FakeScoreScale {
  multiplier: number;
  anchorDisplay: string;
}

const FAKE_SCORE_SCALES: Record<string, FakeScoreScale> = {
  fake: { multiplier: 1, anchorDisplay: '100%' },
  [FAKE_M2_TENANT_ID]: { multiplier: 2, anchorDisplay: '200' },
  [FAKE_MR_TENANT_ID]: { multiplier: 1, anchorDisplay: '100%' },
};

function fakeScoreScale(tenantId?: string): FakeScoreScale {
  return FAKE_SCORE_SCALES[tenantId ?? 'fake'] ?? FAKE_SCORE_SCALES.fake;
}

/** Flagged metric columns for the fake scorecard — the L&L default trio. */
const FAKE_METRIC_COLUMNS: MetricColumn[] = [
  { key: 'metric_1', label: 'Steak Shawarma Upsell' },
  { key: 'metric_2', label: 'Döner Kebab Upsell' },
  { key: 'metric_3', label: 'Dubai Chocolate Upsell' },
];

// Drink column on in fake mode (ADR 0002 U2) — exercises the optional drink
// column + its null-vs-real-0 cells.
const FAKE_DRINK_COLUMN: DrinkColumn = { enabled: true, label: 'Dubai Chocolate' };

interface FakeScorecardRow {
  server_id: string;
  name: string;
  initials: string;
  score: number | null;
  score_tier: string | null;
  score_band: ScoreBand | null;
  greetings: number | null;
  // feat-093 Slice B: visit count ≤ greetings (collapse visible in fake mode).
  // null mirrors the greetings null (0 in-window shifts → "—").
  visit_count: number | null;
  metric_totals: Record<string, number | null>;
  drink_total: number | null;
  // feat-097 Part B: per-visit percents parallel to the counts above (one
  // decimal, present-gated by the backend). null mirrors the count null.
  metric_percents: Record<string, number | null>;
  drink_percent: number | null;
}

const FAKE_SCORECARD_ROWS: FakeScorecardRow[] = [
  {
    server_id: 's-beatriz', name: 'Beatriz Sandoval', initials: 'BS',
    score: 16, score_tier: 'Developing', score_band: 'developing',
    greetings: 84, visit_count: 79, metric_totals: { metric_1: 12, metric_2: 5, metric_3: 3 }, drink_total: 9,
    metric_percents: { metric_1: 15.2, metric_2: 6.3, metric_3: 3.8 }, drink_percent: 11.4,
  },
  {
    // Long name — exercises the truncation row in the mockup.
    server_id: 's-emma', name: 'Emma Rocelis Amaya Perez de la Cruz', initials: 'EC',
    score: 14, score_tier: 'Developing', score_band: 'developing',
    greetings: 61, visit_count: 58, metric_totals: { metric_1: 7, metric_2: 9, metric_3: 2 }, drink_total: 4,
    metric_percents: { metric_1: 12.1, metric_2: 15.5, metric_3: 3.4 }, drink_percent: 6.9,
  },
  {
    // Has shifts but ZERO hits on every metric/drink → real 0s / 0.0% (NOT em-dash).
    server_id: 's-gerald', name: 'Gerald Oliver', initials: 'GO',
    score: 8, score_tier: 'Developing', score_band: 'developing',
    greetings: 37, visit_count: 35, metric_totals: { metric_1: 0, metric_2: 0, metric_3: 0 }, drink_total: 0,
    metric_percents: { metric_1: 0.0, metric_2: 0.0, metric_3: 0.0 }, drink_percent: 0.0,
  },
  {
    // ZERO in-window shifts → everything null → em-dash (count AND percent).
    server_id: 's-denisse', name: 'Denisse Munoz', initials: 'DM',
    score: null, score_tier: null, score_band: null,
    greetings: null, visit_count: null, metric_totals: { metric_1: null, metric_2: null, metric_3: null }, drink_total: null,
    metric_percents: { metric_1: null, metric_2: null, metric_3: null }, drink_percent: null,
  },
  {
    server_id: 's-hanns', name: 'Hanns Chiu', initials: 'HC',
    score: 12, score_tier: 'Developing', score_band: 'developing',
    greetings: 52, visit_count: 50, metric_totals: { metric_1: 4, metric_2: 1, metric_3: 6 }, drink_total: 7,
    metric_percents: { metric_1: 8.0, metric_2: 2.0, metric_3: 12.0 }, drink_percent: 14.0,
  },
];

function fakeScorecardRow(s: FakeScorecardRow, scale: FakeScoreScale): ServerScorecardRow {
  return {
    server_id: s.server_id,
    name: s.name,
    initials: s.initials,
    location_id: 'loc-fake',
    location_name: FAKE_LOCATION,
    score: s.score,
    // feat-234: the Scorecard's score_display is the backend's POOLED weighted
    // rate, PREFORMATTED — it carries a `%` suffix only at tenant multiplier 1
    // (spec A3). This mirrors the backend's `_score_display_pct` so `start:fake`
    // shows what a real environment shows, in BOTH tenant shapes. React renders
    // the string verbatim and never appends the suffix itself.
    score_display:
      s.score === null ? '—' : scale.multiplier === 1 ? `${s.score}%` : `${s.score}`,
    score_tier: s.score_tier,
    score_band: s.score_band,
    greetings: s.greetings,
    visit_count: s.visit_count,
    metric_totals: s.metric_totals,
    drink_total: s.drink_total,
    metric_percents: s.metric_percents,
    drink_percent: s.drink_percent,
  };
}

function fakeResponse(tenantId?: string): ServerScorecardResponse {
  // The SAME rows and the SAME numeric scores under both tenant shapes — only
  // the formatting of `score_display` and the two scale fields differ. That is
  // the point: a reviewer flipping between them sees exactly the difference the
  // multiplier makes, with nothing else moving.
  const scale = fakeScoreScale(tenantId);
  return {
    metric_columns: FAKE_METRIC_COLUMNS,
    drink_column: FAKE_DRINK_COLUMN,
    rows: FAKE_SCORECARD_ROWS.map((r) => fakeScorecardRow(r, scale)),
    score_windowing: 'windowed',
    score_multiplier: scale.multiplier,
    score_scale_anchor_display: scale.anchorDisplay,
  };
}

// ---------- Server-detail fake (Performance History — out of feat-088 scope) ----------

interface FakeServer {
  server_id: string;
  name: string;
  initials: string;
  score: number;
  score_tier: string;
  score_band: ScoreBand;
  total_xp: number;
  total_shifts: number;
  current_streak: number;
  challenges_completed: number;
  badges_earned: number;
  avg_upsells: number;
  avg_tip_pct: number;
}

const FAKE_CHALLENGES_TOTAL = 20;
const FAKE_BADGES_TOTAL = 15;
const FAKE_LOCATION = 'The Grove';

const FAKE_SERVERS: FakeServer[] = [
  {
    server_id: 's-alex', name: 'Alex Williams', initials: 'AW',
    score: 812, score_tier: 'Excellent', score_band: 'excellent',
    total_xp: 8450, total_shifts: 47, current_streak: 5,
    challenges_completed: 12, badges_earned: 8, avg_upsells: 8.4, avg_tip_pct: 22,
  },
  {
    server_id: 's-carlos', name: 'Carlos Rivera', initials: 'CR',
    score: 680, score_tier: 'Good', score_band: 'good',
    total_xp: 7230, total_shifts: 42, current_streak: 3,
    challenges_completed: 10, badges_earned: 7, avg_upsells: 6.1, avg_tip_pct: 19,
  },
  {
    server_id: 's-maria', name: 'Maria Gonzalez', initials: 'MG',
    score: 905, score_tier: 'Exceptional', score_band: 'exceptional',
    total_xp: 11020, total_shifts: 61, current_streak: 9,
    challenges_completed: 17, badges_earned: 11, avg_upsells: 9.7, avg_tip_pct: 24,
  },
  {
    server_id: 's-kevin', name: 'Kevin Park', initials: 'KP',
    score: 540, score_tier: 'Developing', score_band: 'developing',
    total_xp: 4180, total_shifts: 22, current_streak: 0,
    challenges_completed: 4, badges_earned: 3, avg_upsells: 3.2, avg_tip_pct: 16,
  },
];

function fakeDetail(serverId: string, tenantId?: string): ServerDetailResponse {
  const s = FAKE_SERVERS.find((f) => f.server_id === serverId) ?? FAKE_SERVERS[0];
  const scale = fakeScoreScale(tenantId);
  return {
    server_id: s.server_id,
    name: s.name,
    initials: s.initials,
    location_id: 'loc-fake',
    location_name: FAKE_LOCATION,
    score: s.score,
    // feat-234: same preformatted contract as the grid — this endpoint's
    // score_display comes from the same backend helper, so the `%` is present
    // only at multiplier 1. No `score_scale_anchor_display` here: it is a
    // GRID-only field (see the type comment).
    score_display: scale.multiplier === 1 ? `${s.score}%` : `${s.score}`,
    score_multiplier: scale.multiplier,
    score_tier: s.score_tier,
    score_band: s.score_band,
    total_xp: s.total_xp,
    total_shifts: s.total_shifts,
    current_streak: s.current_streak,
    challenges_completed: s.challenges_completed,
    challenges_total: FAKE_CHALLENGES_TOTAL,
    challenges_placeholder: true,
    challenges_data_source: 'seeded_challenges',
    badges_earned: s.badges_earned,
    badges_total: FAKE_BADGES_TOTAL,
    badges_placeholder: true,
    badges_data_source: 'seeded_badges',
    avg_upsells: s.avg_upsells,
    avg_upsells_display: s.avg_upsells.toFixed(1),
    avg_tip_pct: s.avg_tip_pct,
    avg_tip_pct_display: `${s.avg_tip_pct}%`,
    avg_tip_pct_placeholder: true,
    avg_tip_pct_data_source: 'seeded_tip',
    placeholder: false,
    data_source: 'real',
    xp_windowing: 'lifetime',
    shifts_windowing: 'all_time',
    score_windowing: 'windowed',
  };
}

// ---------- Public API ----------

export interface ServerScorecardParams {
  locationId?: string;
  startDate?: string; // YYYY-MM-DD
  endDate?: string; // YYYY-MM-DD
}

export async function getServerScorecard(
  tenantId: string,
  params: ServerScorecardParams = {},
): Promise<ServerScorecardResponse> {
  if (isFake()) {
    // Pass the tenant through so the multiplier-2 fake shape is reachable
    // (`?tenant=fake-m2`), not just the pilot's multiplier-1 one.
    // feat-317: `?tenant=fake-mr` is the two-role tenant (one grid per role).
    if (tenantId === FAKE_MR_TENANT_ID) return fakeMrScorecardResponse();
    return withOneFakeGroup(fakeResponse(tenantId));
  }
  const qs = new URLSearchParams();
  if (params.locationId) qs.set('location_id', params.locationId);
  if (params.startDate) qs.set('start_date', params.startDate);
  if (params.endDate) qs.set('end_date', params.endDate);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return api<ServerScorecardResponse>(
    `/api/server-scorecard/${encodeURIComponent(tenantId)}${suffix}`,
  );
}

export interface ServerDetailParams {
  startDate?: string; // YYYY-MM-DD
  endDate?: string; // YYYY-MM-DD
}

export async function getServerDetail(
  tenantId: string,
  serverId: string,
  params: ServerDetailParams = {},
): Promise<ServerDetailResponse> {
  if (isFake()) {
    // feat-234 / QA N1: pass the tenant through. `fakeDetail` already branches on
    // it; dropping it here made that branch dead code and made `?tenant=fake-m2`
    // honest on the Scorecard but not one click later.
    const d = fakeDetail(serverId, tenantId);
    return {
      ...d,
      groups: [{
        ...FAKE_SINGLE_ROLE_HEADER,
        score: d.score,
        score_display: d.score_display,
        score_multiplier: d.score_multiplier,
        score_scale_anchor_display: fakeScoreScale(tenantId).anchorDisplay,
        score_tier: d.score_tier,
        score_band: d.score_band,
      }],
    };
  }
  const qs = new URLSearchParams();
  if (params.startDate) qs.set('start_date', params.startDate);
  if (params.endDate) qs.set('end_date', params.endDate);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return api<ServerDetailResponse>(
    `/api/server-scorecard/${encodeURIComponent(tenantId)}/${encodeURIComponent(serverId)}${suffix}`,
  );
}

// ════════════════════════════════════════════════════════════════════════════
// feat-091 — Recent Shifts per-shift listing (parity with the Server Scorecard).
//
// Client for GET /api/server-scorecard/{tenant_id}/{server_id}/shifts
// (routes/server_scorecard.py, ADR 0001 D-1/D-6). The endpoint assembles each
// row ENTIRELY from the frozen `shift.score_breakdown` snapshot, so the frontend
// renders columns in returned order and every value verbatim — ZERO metric math
// in React (`feedback_metrics_single_source`, AC#11). The Score tooltip
// (`score_tooltip`) is pre-rendered server-side; the FE only lays it out.
// ════════════════════════════════════════════════════════════════════════════

/** A row's score status (ADR 0001 §D-6b) — the authoritative field the FE reads
 *  intent from; never inferred from nulls.
 *   - "scored"          — normal (score + breakdown present).
 *   - "pending"         — score IS NULL but a prompt version is recorded.
 *   - "version_unknown" — prompt_template_version_id IS NULL (suppress the score). */
export type ShiftScoreStatus = 'scored' | 'pending' | 'version_unknown';

/** One Recent-Shifts column header. The full prompt-configured metric set in
 *  `display_order`; `is_scoring` drives the ★ header treatment (ADR D-3/D-6).
 *  The drink column descriptor uses this same shape. */
export interface RecentShiftColumn {
  key: string;
  label: string;
  display_order: number;
  is_scoring: boolean;
}

/** A per-cell value for one shift × one column, read from THAT shift's frozen
 *  snapshot. `present=false` (→ "—") when the column's metric is absent from the
 *  shift's snapshot (e.g. a metric added after the shift, or an unscored shift). */
export interface RecentShiftMetricValue {
  percent: number | null;
  count: number | null;
  present: boolean;
}

/** One row of the Score-cell tooltip breakdown table (ADR D-4). No "weight"
 *  column — the canonical formula is a rate, not a weighted sum (ADR F2). */
export interface ScoreTooltipMetric {
  label: string;
  count: number;
  percent: number;
}

/** Ready-to-render Score-cell tooltip, assembled server-side (ADR D-4 / AC#11).
 *  null on non-scored rows (the FE shows a plain status message instead). */
export interface ScoreTooltipData {
  basis: string;
  scoring_metrics: ScoreTooltipMetric[];
  attempt_count: number | null;
  greetings: number;
  // feat-093 Slice B: per-shift visit count (feat-092 `breakdown.visit_count`,
  // AC10 fallback → greetings). Additive; backend single-sources it.
  visit_count?: number | null;
  rate_percent: number | null;
  score_multiplier: number;
  formula: string | null;
  formula_human: string;
  prompt_version_id: string | null;
  version_note: string;
}

/** One shift in the window — assembled from the frozen snapshot (ADR D-1/D-2). */
export interface RecentShiftRow {
  shift_id: string;
  shift_date: string; // YYYY-MM-DD
  shift_label: string; // type/role label, e.g. "Lunch"
  score_status: ShiftScoreStatus;
  score: number | null;
  // feat-234: preformatted per-shift score — "100%" | "—" (bare integer when the
  // tenant's score_multiplier isn't 1). The per-shift score VALUE is unchanged by
  // feat-234; only its formatting is. Rendered verbatim.
  score_display: string;
  scored: boolean; // == (score_status === 'scored'), kept consistent server-side
  prompt_version_id: string | null;
  greetings: number | null;
  // feat-093 Slice B: per-shift visit count (feat-092 `breakdown.visit_count`,
  // AC10 fallback → greetings). Optional because pre-recalc `v:2` breakdowns omit
  // it; the "Visits" cell reads `visit_count ?? greetings`. Backend single-sources
  // it — React never recomputes. Same null semantics as `greetings`.
  visit_count?: number | null;
  metrics: Record<string, RecentShiftMetricValue>; // {} on non-scored rows
  drink: RecentShiftMetricValue | null;
  score_tooltip: ScoreTooltipData | null; // null on non-scored rows
}

export interface ServerShiftsResponse {
  server_id: string;
  name: string;
  metric_columns: RecentShiftColumn[];
  drink_column: RecentShiftColumn | null;
  rows: RecentShiftRow[];
  // feat-317 (ADR §6.3): one Recent Shifts grid per effective role.
  groups?: ServerShiftsGroup[];
}

/** feat-317 §6.3 — G4 header + today's Recent Shifts fields, scoped to one role. */
export interface ServerShiftsGroup extends GroupHeader {
  server_id: string;
  name: string;
  metric_columns: RecentShiftColumn[];
  drink_column: RecentShiftColumn | null;
  rows: RecentShiftRow[];
}

export interface ServerShiftsParams {
  startDate?: string; // YYYY-MM-DD
  endDate?: string; // YYYY-MM-DD
}

// ---------- Fake-mode data (matches the design mockup states) ----------
// Exercises every enumerated state: scored happy, the "Score 0 explained" hero,
// a metric added after the shift (present:false → "—"), a pending (never-scored)
// row, and a version_unknown (legacy, no recorded prompt version) row. Columns
// interleave scoring (★) and reference metrics so the header treatment shows.

const FAKE_SHIFT_COLUMNS: RecentShiftColumn[] = [
  { key: 'metric_1', label: 'Steak Shawarma Upsell', display_order: 1, is_scoring: true },
  { key: 'metric_2', label: 'Döner Kebab Upsell', display_order: 2, is_scoring: true },
  { key: 'metric_q', label: 'Greeting Quality', display_order: 3, is_scoring: false },
];

const FAKE_SHIFT_DRINK_COLUMN: RecentShiftColumn = {
  key: 'drink_promotion',
  label: 'Dubai Chocolate',
  display_order: 4,
  is_scoring: true,
};

function fakeScoredTooltip(
  scoring: ScoreTooltipMetric[],
  attempts: number,
  greetings: number,
  visitCount: number,
  multiplier: number,
  score: number,
  versionId: string,
): ScoreTooltipData {
  // Rate is computed against visits (feat-092 denominator) so the fake formula
  // string matches the backend's. This is FAKE-DATA construction, not a render-
  // path recompute — the real backend supplies `formula_human` verbatim.
  const rate = visitCount > 0 ? Math.round((attempts / visitCount) * 100) : 0;
  return {
    basis: 'upsell_attempt_rate',
    scoring_metrics: scoring,
    attempt_count: attempts,
    greetings,
    visit_count: visitCount,
    rate_percent: rate,
    score_multiplier: multiplier,
    formula: 'score_multiplier * round(scorecard_attempt_count / visit_count * 100)',
    formula_human: `round(${attempts} ÷ ${visitCount} × 100) = ${rate}%  ×  ${multiplier.toFixed(1)}  =  ${score}`,
    prompt_version_id: versionId,
    version_note: `Scored under prompt version ${versionId}`,
  };
}

function fakeServerShiftsResponse(
  serverId: string,
  name: string,
  tenantId?: string,
): ServerShiftsResponse {
  // feat-234 / QA N1: the per-shift `score_display` is preformatted by the same
  // backend helper as every other score, so it carries the `%` only at tenant
  // multiplier 1. These rows used to hardcode the `%`, which made Recent Shifts
  // show "75%" for a multiplier-2 tenant whose Scorecard — one click earlier —
  // correctly showed a bare integer.
  const scale = fakeScoreScale(tenantId);
  const sd = (score: number | null): string =>
    score === null ? '—' : scale.multiplier === 1 ? `${score}%` : `${score}`;

  // feat-234 / QA N5: DERIVE each row's score from that row's own attempts and
  // visits, at the tenant's multiplier — `multiplier × round(attempts ÷ visits
  // × 100)`, the real formula. Two reasons it must be derived rather than typed
  // in:
  //
  //  1. A score is only ACHIEVABLE at multiplier 2 if it is even. The old
  //     hardcoded 75 and 25 are arithmetically impossible there, so once N1 made
  //     this fixture tenant-switchable a reviewer could reach a screen showing
  //     `75`, do the manager's arithmetic (75 ÷ 200 = 0.375 attempts per
  //     customer) and get a plausible, impossible answer. Deriving makes every
  //     m=2 score even for free.
  //  2. Four of the five rows were ALREADY incoherent with their own tooltips —
  //     e.g. fk-0a rendered `round(3 ÷ 4 × 100) = 75%  ×  2.0  =  75`, a line
  //     that visibly does not multiply out, on the surface whose entire job is
  //     showing the manager how the score was reached.
  //
  // This is FAKE-DATA construction mirroring the backend formula, not a
  // render-path recompute — the real backend sends `score` and `formula_human`
  // and React renders them verbatim.
  const scoreOf = (attempts: number, visits: number): number =>
    scale.multiplier * (visits > 0 ? Math.round((attempts / visits) * 100) : 0);
  return {
    server_id: serverId,
    name,
    metric_columns: FAKE_SHIFT_COLUMNS,
    drink_column: FAKE_SHIFT_DRINK_COLUMN,
    rows: [
      // feat-096: extra dated scored rows so the trend charts read as a trend
      // (≥6 dated points) with varied percentages. A metric `present:false` on
      // fk-0b exercises a Hospitality-series gap; drink percentages vary so the
      // Upsell drink line isn't flat.
      {
        shift_id: 'fk-0a', shift_date: '2026-06-20', shift_label: 'Dinner',
        score_status: 'scored', score: scoreOf(3, 4), score_display: sd(scoreOf(3, 4)), scored: true,
        prompt_version_id: 'pv-active',
        greetings: 6, visit_count: 4,
        metrics: {
          metric_1: { percent: 50.0, count: 2, present: true },
          metric_2: { percent: 25.0, count: 1, present: true },
          metric_q: { percent: 100.0, count: 4, present: true },
        },
        drink: { percent: 50.0, count: 2, present: true },
        score_tooltip: fakeScoredTooltip(
          [{ label: 'Steak Shawarma Upsell', count: 2, percent: 50.0 },
           { label: 'Döner Kebab Upsell', count: 1, percent: 25.0 }],
          3, 6, 4, scale.multiplier, scoreOf(3, 4), 'pv-active',
        ),
      },
      {
        shift_id: 'fk-0b', shift_date: '2026-06-17', shift_label: 'Lunch',
        score_status: 'scored', score: scoreOf(1, 4), score_display: sd(scoreOf(1, 4)), scored: true,
        prompt_version_id: 'pv-active',
        greetings: 5, visit_count: 4,
        metrics: {
          metric_1: { percent: 25.0, count: 1, present: true },
          metric_2: { percent: 0.0, count: 0, present: true },
          // Reference metric absent from this shift's snapshot → Hospitality gap.
          metric_q: { percent: null, count: null, present: false },
        },
        drink: { percent: 25.0, count: 1, present: true },
        score_tooltip: fakeScoredTooltip(
          [{ label: 'Steak Shawarma Upsell', count: 1, percent: 25.0 }],
          1, 5, 4, scale.multiplier, scoreOf(1, 4), 'pv-active',
        ),
      },
      // Healthy non-zero score.
      {
        shift_id: 'fk-1', shift_date: '2026-06-14', shift_label: 'Dinner',
        score_status: 'scored', score: scoreOf(2, 3), score_display: sd(scoreOf(2, 3)), scored: true,
        prompt_version_id: 'pv-active',
        greetings: 4, visit_count: 3,
        metrics: {
          metric_1: { percent: 25.0, count: 1, present: true },
          metric_2: { percent: 25.0, count: 1, present: true },
          metric_q: { percent: 75.0, count: 3, present: true },
        },
        drink: { percent: 0.0, count: 0, present: true },
        score_tooltip: fakeScoredTooltip(
          [{ label: 'Steak Shawarma Upsell', count: 1, percent: 25.0 },
           { label: 'Döner Kebab Upsell', count: 1, percent: 25.0 }],
          2, 4, 3, scale.multiplier, scoreOf(2, 3), 'pv-active',
        ),
      },
      // The hero "Score 0 explained": reference metric high, scoring metrics 0.
      {
        shift_id: 'fk-2', shift_date: '2026-06-11', shift_label: 'Lunch',
        score_status: 'scored', score: scoreOf(0, 3), score_display: sd(scoreOf(0, 3)), scored: true,
        prompt_version_id: 'pv-active',
        greetings: 4, visit_count: 3,
        metrics: {
          metric_1: { percent: 0.0, count: 0, present: true },
          metric_2: { percent: 0.0, count: 0, present: true },
          metric_q: { percent: 25.0, count: 1, present: true },
        },
        drink: { percent: 0.0, count: 0, present: true },
        score_tooltip: fakeScoredTooltip([], 0, 4, 3, scale.multiplier, scoreOf(0, 3), 'pv-active'),
      },
      // Metric added AFTER this shift → absent from its snapshot (present:false → "—").
      {
        shift_id: 'fk-3', shift_date: '2026-06-08', shift_label: 'Dinner',
        score_status: 'scored', score: scoreOf(1, 7), score_display: sd(scoreOf(1, 7)), scored: true,
        prompt_version_id: 'pv-old',
        greetings: 8, visit_count: 7,
        metrics: {
          metric_1: { percent: 12.5, count: 1, present: true },
          metric_2: { percent: null, count: null, present: false },
          metric_q: { percent: 50.0, count: 4, present: true },
        },
        drink: { percent: 0.0, count: 0, present: true },
        score_tooltip: fakeScoredTooltip(
          [{ label: 'Steak Shawarma Upsell', count: 1, percent: 12.5 }],
          1, 8, 7, scale.multiplier, scoreOf(1, 7), 'pv-old',
        ),
      },
      // Pending — never scored (score NULL, version recorded). Whole row "—".
      {
        shift_id: 'fk-4', shift_date: '2026-06-05', shift_label: 'Lunch',
        score_status: 'pending', score: null, score_display: '—', scored: false,
        prompt_version_id: 'pv-active',
        greetings: null, metrics: {}, drink: null, score_tooltip: null,
      },
      // Version unknown — legacy shift with no recorded prompt version. Whole row "—".
      {
        shift_id: 'fk-5', shift_date: '2026-06-02', shift_label: 'Dinner',
        score_status: 'version_unknown', score: null, score_display: '—', scored: false,
        prompt_version_id: null,
        greetings: null, metrics: {}, drink: null, score_tooltip: null,
      },
    ],
  };
}

/**
 * Fetch the per-shift Recent Shifts listing for one server in a date window.
 * Rows are most-recent-first. In fake mode returns deterministic canned data in
 * the real contract shape (covers all design states).
 */
export async function getServerRecentShifts(
  tenantId: string,
  serverId: string,
  params: ServerShiftsParams = {},
): Promise<ServerShiftsResponse> {
  if (isFake()) {
    if (tenantId === FAKE_MR_TENANT_ID) return fakeMrServerShiftsResponse(serverId, 'Alex Williams');
    return withOneFakeGroup(fakeServerShiftsResponse(serverId, 'Alex Williams', tenantId));
  }
  const qs = new URLSearchParams();
  if (params.startDate) qs.set('start_date', params.startDate);
  if (params.endDate) qs.set('end_date', params.endDate);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return api<ServerShiftsResponse>(
    `/api/server-scorecard/${encodeURIComponent(tenantId)}/${encodeURIComponent(serverId)}/shifts${suffix}`,
  );
}

// ════════════════════════════════════════════════════════════════════════════
// feat-093 Slice C — Store/Manager per-shift scored rows (single-source migration).
//
// Client for GET /api/server-scorecard/{tenant_id}/shifts (ADR 0015 §Slice C).
// This is the manager/corporate-scoped, per-(server-shift) scored-rows endpoint
// that replaces the four Class-2 surfaces' client-side `Math.max`/`computeScore`
// recompute. The frontend renders every value VERBATIM — score, visit_count, and
// per-metric percents all come from the backend (feedback_metrics_single_source,
// AC-C3/C4). The ShiftDetails surfaces pass `shift_id` (a manager-shift id) to
// drill into one shift; the Store/Manager rollups group the flat rows by
// `manager_shift_id` and display-average the BACKEND per-shift scores (averaging
// backend numbers, never re-deriving a rate — allowed under single-source).
//
// RBAC is enforced server-side: Manager is pinned to their own location (the
// `location_id` param cannot widen it), Corporate/Admin scope by tenant, foreign
// tenant 404s (fail-closed). The client surfaces a 404/403 as the same
// ServerScorecardForbiddenError the rest of this module uses.
// ════════════════════════════════════════════════════════════════════════════

/** One row of the Store/Manager shifts endpoint — one per (server-shift) in the
 *  window. Every numeric field is backend-sourced; React renders it verbatim. */
export interface StoreShiftRow {
  shift_id: string; // the server shift id
  manager_shift_id: string | null; // for Store/Manager grouping client-side
  server_id: string;
  server_name: string;
  manager_id: string | null;
  manager_name: string | null;
  location_id: string;
  location_name: string;
  shift_date: string; // YYYY-MM-DD
  shift_label: string;
  score_status: ShiftScoreStatus;
  score: number | null; // persisted shift.score — verbatim
  // feat-234 deliberately does NOT touch this one: the STORE shift surface keeps
  // a bare integer with no `%`. Only the Scorecard grid, the perf-history detail,
  // and Recent Shifts gained the suffix. Nobody asked to change the store view.
  score_display: string;
  scored: boolean;
  // feat-092 `breakdown.visit_count` with the AC10 `greetings` fallback applied
  // server-side. NULLISH (??) at the render site so a real 0 shows 0. Backend is
  // the single source — React never recomputes it.
  visit_count: number | null;
  greetings: number | null; // breakdown.greetings (retained)
  metrics: Record<string, RecentShiftMetricValue>; // per active schema
  drink: RecentShiftMetricValue | null;
}

export interface StoreShiftsResponse {
  metric_columns: MetricColumn[];
  drink_column: MetricColumn | null;
  rows: StoreShiftRow[];
  // feat-317 (ADR §6.4): one Manager/Store table per effective role.
  groups?: StoreShiftsGroup[];
}

/** feat-317 §6.4 — G4 header + today's store-shift fields, scoped to one role. */
export interface StoreShiftsGroup extends GroupHeader {
  metric_columns: MetricColumn[];
  drink_column: MetricColumn | null;
  rows: StoreShiftRow[];
}

export interface StoreShiftsParams {
  /** The ShiftDetails drill-in: restrict to one manager shift. */
  shiftId?: string;
  /** Corporate/Admin only — a store Manager is pinned to their location server-side. */
  locationId?: string;
  startDate?: string; // YYYY-MM-DD
  endDate?: string; // YYYY-MM-DD
}

// ---------- Fake-mode data (Store/Manager shifts) ----------
// Mirrors the real contract. Two manager-shifts so the Store/Manager grouping +
// backend-score display-average is exercised; each visit_count ≤ greetings so the
// feat-092 collapse is visible. Covers a real 0 (visit_count 0) and a non-scored
// row (null score/visit_count → "—").

const FAKE_STORE_SHIFT_COLUMNS: MetricColumn[] = [
  { key: 'metric_1', label: 'Steak Shawarma Upsell' },
  { key: 'metric_2', label: 'Döner Kebab Upsell' },
  { key: 'metric_3', label: 'Dubai Chocolate Upsell' },
];

const FAKE_STORE_SHIFT_DRINK_COLUMN: MetricColumn = {
  key: 'drink_promotion',
  label: 'Dubai Chocolate',
};

function fakeStoreShiftRow(o: Partial<StoreShiftRow> & Pick<StoreShiftRow, 'shift_id' | 'server_name'>): StoreShiftRow {
  return {
    manager_shift_id: 'mgr-shift-1',
    server_id: `srv-${o.shift_id}`,
    manager_id: 'mgr-1',
    manager_name: 'Sarah Johnson',
    location_id: 'loc-fake',
    location_name: FAKE_LOCATION,
    shift_date: '2026-06-14',
    shift_label: 'Dinner',
    score_status: 'scored',
    score: 50,
    score_display: '50',
    scored: true,
    visit_count: 3,
    greetings: 4,
    metrics: {
      metric_1: { percent: 25.0, count: 1, present: true },
      metric_2: { percent: 25.0, count: 1, present: true },
      metric_3: { percent: 0.0, count: 0, present: true },
    },
    drink: { percent: 0.0, count: 0, present: true },
    ...o,
  };
}

function fakeStoreShiftsResponse(params: StoreShiftsParams): StoreShiftsResponse {
  const allRows: StoreShiftRow[] = [
    fakeStoreShiftRow({
      shift_id: 'fk-1', server_name: 'Beatriz Sandoval',
      manager_shift_id: 'mgr-shift-1', score: 50, score_display: '50',
      greetings: 4, visit_count: 3,
    }),
    fakeStoreShiftRow({
      shift_id: 'fk-2', server_name: 'Gerald Oliver',
      manager_shift_id: 'mgr-shift-1', score: 8, score_display: '8',
      greetings: 5, visit_count: 0, // real 0 visits → must render 0, not greetings
      metrics: {
        metric_1: { percent: 0.0, count: 0, present: true },
        metric_2: { percent: 0.0, count: 0, present: true },
        metric_3: { percent: 0.0, count: 0, present: true },
      },
      drink: { percent: 0.0, count: 0, present: true },
    }),
    fakeStoreShiftRow({
      shift_id: 'fk-3', server_name: 'Hanns Chiu',
      manager_shift_id: 'mgr-shift-2', shift_date: '2026-06-11', score: 25, score_display: '25',
      greetings: 8, visit_count: 7,
      metrics: {
        metric_1: { percent: 12.5, count: 1, present: true },
        metric_2: { percent: null, count: null, present: false },
        metric_3: { percent: 25.0, count: 2, present: true },
      },
      drink: { percent: 0.0, count: 0, present: true },
    }),
    fakeStoreShiftRow({
      shift_id: 'fk-4', server_name: 'Denisse Munoz',
      manager_shift_id: 'mgr-shift-2', shift_date: '2026-06-11',
      score_status: 'pending', score: null, score_display: '—', scored: false,
      greetings: null, visit_count: null, metrics: {}, drink: null,
    }),
  ];
  // Honor the ShiftDetails drill-in so fake mode mirrors the real filter.
  const rows = params.shiftId
    ? allRows.filter((r) => r.manager_shift_id === params.shiftId)
    : allRows;
  return {
    metric_columns: FAKE_STORE_SHIFT_COLUMNS,
    drink_column: FAKE_STORE_SHIFT_DRINK_COLUMN,
    rows,
  };
}

/**
 * Fetch per-(server-shift) scored rows for the Store/Manager scorecard family.
 * In fake mode returns deterministic canned data in the real contract shape.
 */
export async function getStoreShifts(
  tenantId: string,
  params: StoreShiftsParams = {},
): Promise<StoreShiftsResponse> {
  if (isFake()) {
    if (tenantId === FAKE_MR_TENANT_ID) return fakeMrStoreShiftsResponse(params);
    return withOneFakeGroup(fakeStoreShiftsResponse(params));
  }
  const qs = new URLSearchParams();
  if (params.locationId) qs.set('location_id', params.locationId);
  if (params.startDate) qs.set('start_date', params.startDate);
  if (params.endDate) qs.set('end_date', params.endDate);
  if (params.shiftId) qs.set('shift_id', params.shiftId);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return api<StoreShiftsResponse>(
    `/api/server-scorecard/${encodeURIComponent(tenantId)}/shifts${suffix}`,
  );
}

// ════════════════════════════════════════════════════════════════════════════
// feat-097 Part D — benchmark↔post windowed comparison (ADR 0001 Decision 2).
//
// Client for GET /api/server-scorecard/{tenant_id}/{server_id}/comparison. The
// backend resolves the two windows from PERIOD CONFIG (no params), computes each
// metric's per-visit % over each window with a PRESENT-GATED denominator, and the
// percentage-point delta — all server-side, ONCE (feedback_metrics_single_source).
// React renders `benchmark.percent` / `post.percent` / `delta_pp` and the window
// context VERBATIM; it never aggregates or subtracts. Always-% (the Part B/C #/%
// toggle does NOT apply here — counts are meaningless across two differently-sized
// windows). Grouping is derived from `is_scoring` (+ drink_comparison → Upsell),
// never hardcoded keys (spec D1).
// ════════════════════════════════════════════════════════════════════════════

/** One window's resolved bounds + headline context (echoed from the backend —
 *  the PERIOD CONFIG dates are NOT hardcoded in React). `visit_count` is the total
 *  ungated visit sum over the window's shifts (context only, e.g. "100 visits"),
 *  NOT a per-metric denominator. */
export interface ComparisonWindow {
  start_date: string; // YYYY-MM-DD (inclusive)
  end_date: string; // YYYY-MM-DD (inclusive, resolved/observed)
  shift_count: number;
  visit_count: number;
}

/** One metric's per-visit aggregate within ONE window. `visits` is the
 *  present-gated denominator; `percent` = round(count/visits*100, 1) unclamped, or
 *  null when visits == 0 (no present shift → "—", never divide-by-zero).
 *
 *  feat-101 A2 (ADR 0019 Amendment A2, additive) — `percent` is the VISIT-WEIGHTED
 *  rate (Σcount/Σvisits, "Weighted" toggle position). `percent_avg` is the NEW
 *  unweighted mean of each present shift's own metric % ("Avg" position):
 *  mean(shift_count/shift_visits*100) over shifts where the metric is present with
 *  visits>0, rounded once. null when no such shift. Optional so a pre-A2 backend
 *  degrades to "—" under Avg mode; the default Weighted view reads `percent` only.
 *  Rendered VERBATIM — React never averages (feedback_metrics_single_source). */
export interface ComparisonMetricValue {
  count: number;
  visits: number;
  percent: number | null;
  percent_avg?: number | null;
}

/** One metric (or the drink column) compared across the two windows. `is_scoring`
 *  drives the Hospitality (¬is_scoring) / Upsell (is_scoring + drink) grouping.
 *  `delta_pp` is the backend-computed percentage-point change (post − benchmark),
 *  null when either side's percent is null — rendered verbatim, never subtracted in React. */
export interface ComparisonRow {
  key: string;
  label: string;
  is_scoring: boolean;
  benchmark: ComparisonMetricValue;
  post: ComparisonMetricValue;
  delta_pp: number | null;
  // feat-101 A2 (additive) — the Avg-basis delta: _delta_pp(post.percent_avg,
  // benchmark.percent_avg). OPTIONAL (pre-A2 backend omits → "—" under Avg mode);
  // null when either side's percent_avg is null. Rendered verbatim, never subtracted in React.
  delta_pp_avg?: number | null;
}

export interface ComparisonWindows {
  benchmark: ComparisonWindow;
  post: ComparisonWindow;
}

/** One side's rolled-up value for a section (Hospitality / Upsell). `percent` is the
 *  backend-computed arithmetic mean of child %s (feat-100; null sides skipped). null
 *  when the section has no usable rows on that side → FE renders "—" (never a
 *  fabricated 0).
 *
 *  feat-101 (ADR 0019, additive): `percent_weighted` = the visit-weighted average
 *  `_gated_percent(Σ child count, Σ child present-gated visits)` summed over ALL
 *  children (zero-visit children contribute (0,0)); `count`/`visits` are those Σ
 *  sums (the # mode numerator/denominator AND the weighted inputs). Because
 *  per-metric `visits` are present-gated they differ within a group, so
 *  `percent_weighted` ≠ `percent` in general (the toggle is meaningful). All three
 *  are OPTIONAL so a pre-feat-101 backend (no weighted/count/visits) degrades to "—"
 *  when those toggles are flipped — the default `%`+`avg` view reads `percent` only.
 *  `percent_weighted`/`count`/`visits` null when that side's Σvisits = 0. Rendered
 *  VERBATIM — React never re-derives them (feedback_metrics_single_source). */
export interface GroupRollupValue {
  percent: number | null;
  percent_weighted?: number | null;
  count?: number | null;
  visits?: number | null;
  // feat-101 A2 (additive) — the "Avg" rollup: unweighted mean of the section's
  // children `percent_avg` values (skip nulls; null if all null). Reconciles
  // visually — under Avg+% the rollup equals the average of the displayed rows
  // (ADR 0019 A2-D3). The old `percent` (cross-metric mean of weighted %s) is
  // superseded and no longer read by the FE. null when no child has a percent_avg.
  percent_avg?: number | null;
}

/** feat-100 Part 2 — additive per-section rollup served by /comparison. `is_scoring`
 *  selects the section (false → Hospitality, true → Upsell), mirroring how the rows
 *  themselves are grouped — never matched by the `id` string. `benchmark.percent` /
 *  `post.percent` are the backend's SIMPLE MEAN of that section's child %s per side
 *  (null sides skipped); `delta_pp` = post_mean − bench_mean. All computed server-side,
 *  ONCE — React renders verbatim and never reaches into `comparisons[]` to derive them
 *  (feedback_metrics_single_source). `row_count` is the number of metric rows in the
 *  section (context only). */
export interface GroupRollup {
  id: string; // "hospitality" | "upsell" (display/debug only — grouping is by is_scoring)
  is_scoring: boolean;
  benchmark: GroupRollupValue;
  post: GroupRollupValue;
  delta_pp: number | null;
  // feat-101 (ADR 0019, additive) — the weighted-mean Δ:
  // `_delta_pp(post.percent_weighted, benchmark.percent_weighted)`. OPTIONAL (pre-feat-101
  // backend omits it → "—" under weighted mean-mode); null when either side's
  // `percent_weighted` is null. Rendered verbatim — never subtracted in React.
  delta_pp_weighted?: number | null;
  // feat-101 A2 (additive) — the Avg-rollup Δ: _delta_pp(post.percent_avg,
  // benchmark.percent_avg) over the rollup's avg %s. null when either side's
  // rollup percent_avg is null.
  delta_pp_avg?: number | null;
  row_count: number;
}

export interface ServerComparisonResponse {
  server_id: string;
  name: string;
  // Active schema's full metric column set (ordered, with is_scoring) — same model
  // as /shifts. The grouping/labels come from the comparison rows themselves; this
  // is carried for parity with the table.
  metric_columns: RecentShiftColumn[];
  drink_column: RecentShiftColumn | null;
  windows: ComparisonWindows;
  comparisons: ComparisonRow[];
  drink_comparison: ComparisonRow | null;
  // feat-100 Part 2 — additive section rollups (Hospitality / Upsell). Optional so a
  // pre-feat-100 backend response (no rollups) degrades gracefully: a collapsed
  // section with no rollup renders "—" cells rather than crashing or fabricating a mean.
  group_rollups?: GroupRollup[];
  // feat-317 (ADR §6.5): one comparison per effective role present in EITHER
  // window. (Not to be confused with `group_rollups`, the feat-100 sections.)
  groups?: ServerComparisonGroup[];
}

/** feat-317 §6.5 — G4 header + today's comparison fields, scoped to one role.
 *  A full ServerComparisonResponse-compatible object (G5). */
export interface ServerComparisonGroup extends GroupHeader {
  server_id: string;
  name: string;
  metric_columns: RecentShiftColumn[];
  drink_column: RecentShiftColumn | null;
  windows: ComparisonWindows;
  comparisons: ComparisonRow[];
  drink_comparison: ComparisonRow | null;
  group_rollups?: GroupRollup[];
}

// ---------- Fake-mode data (matches the design-lead mockup states) ----------
// Happy path (both windows populated, up/down/flat deltas) + State 2 (a metric
// with no post-window data → post.percent null, delta_pp null). The drink row is
// always-scoring (Upsell group). Numbers mirror benchmark-comparison-block.html.

function fakeComparisonValue(
  percent: number | null,
  visits: number,
  percentAvg: number | null = null,
): ComparisonMetricValue {
  // count is the # numerator (rendered in # mode); derive a plausible integer from
  // the WEIGHTED percent (count/visits == percent). `percent_avg` is independent
  // (a mean of per-shift rates), set explicitly so Avg ≠ Weighted is visible.
  const count = percent === null ? 0 : Math.round((percent / 100) * visits);
  return { count, visits, percent, percent_avg: percentAvg };
}

function fakeComparisonResponse(serverId: string): ServerComparisonResponse {
  const metricColumns: RecentShiftColumn[] = [
    { key: 'greeting', label: 'Greeting', display_order: 1, is_scoring: false },
    { key: 'polite_order_ask', label: 'Polite Order Ask', display_order: 2, is_scoring: false },
    { key: 'order_readback', label: 'Order Read-back', display_order: 3, is_scoring: false },
    { key: 'metric_1', label: 'Steak Shawarma Upsell', display_order: 4, is_scoring: true },
    { key: 'metric_2', label: 'Döner Kebab Upsell', display_order: 5, is_scoring: true },
    { key: 'metric_3', label: 'Dubai Chocolate Upsell', display_order: 6, is_scoring: true },
  ];
  const drinkColumn: RecentShiftColumn = {
    key: 'drink_promotion',
    label: 'Drink Promotions',
    display_order: 7,
    is_scoring: true,
  };
  // feat-101 A2 — per side a row carries BOTH the weighted `percent` (Σcount/Σvisits,
  // = count/visits) AND the new `percent_avg` (unweighted mean of per-shift rates).
  // They differ here (non-uniform per-shift visits in reality) so the Avg/Weighted
  // toggle visibly changes EVERY row. Deltas are derived (post − bench) per side,
  // matching the backend's _delta_pp. `#` mode renders count/visits (the weighted basis).
  const r1 = (n: number) => Math.round(n * 10) / 10;
  const row = (
    col: RecentShiftColumn,
    benchPct: number | null,
    benchVisits: number,
    benchAvg: number | null,
    postPct: number | null,
    postVisits: number,
    postAvg: number | null,
  ): ComparisonRow => ({
    key: col.key,
    label: col.label,
    is_scoring: col.is_scoring,
    benchmark: fakeComparisonValue(benchPct, benchVisits, benchAvg),
    post: fakeComparisonValue(postPct, postPct === null ? 0 : postVisits, postAvg),
    delta_pp: benchPct === null || postPct === null ? null : r1(postPct - benchPct),
    delta_pp_avg: benchAvg === null || postAvg === null ? null : r1(postAvg - benchAvg),
  });
  return {
    server_id: serverId,
    name: 'Alex Williams',
    metric_columns: metricColumns,
    drink_column: drinkColumn,
    windows: {
      benchmark: { start_date: '2026-06-15', end_date: '2026-06-19', shift_count: 7, visit_count: 100 },
      post: { start_date: '2026-06-20', end_date: '2026-06-24', shift_count: 12, visit_count: 240 },
    },
    comparisons: [
      // Hospitality (non-scoring): up, down, and a no-post-data (State 2) row.
      //              col            bPct bVis bAvg  pPct pVis pAvg
      row(metricColumns[0], 78.0, 100, 74.0, 86.0, 240, 81.0), // weighted 78/86 vs avg 74/81
      row(metricColumns[1], 64.0, 50, 60.0, 59.0, 120, 57.0),
      row(metricColumns[2], 50.0, 20, 47.0, null, 0, null), // State 2: no post-window data → "—"
      // Upsell (scoring): up, up, flat.
      row(metricColumns[3], 10.0, 100, 12.0, 25.0, 240, 22.0),
      row(metricColumns[4], 22.0, 40, 19.0, 31.0, 96, 34.0),
      row(metricColumns[5], 18.0, 30, 16.0, 18.0, 72, 20.0), // weighted flat; avg moves (16→20)
    ],
    drink_comparison: row(drinkColumn, 30.0, 60, 33.0, 44.0, 144, 40.0),
    // feat-100/101/A2 — backend-computed section rollups, rendered VERBATIM (never
    // recomputed from comparisons[]). Per side:
    //   percent          — EXISTING orphaned cross-metric mean (A2-D3: FE no longer reads it).
    //   percent_weighted — Σcount/Σvisits over ALL children (Weighted rollup):
    //     Hosp bench 120/170=70.6, post 277/360=76.9 → Δw 6.3; Upsell bench 42/230=18.3, post 166/552=30.1 → Δw 11.8.
    //   percent_avg      — A2: unweighted mean of children's percent_avg (Avg rollup; = avg of displayed rows):
    //     Hosp bench mean(74,60,47)=60.3, post mean(81,57)=69.0 → Δavg 8.7; Upsell bench mean(12,19,16,33)=20.0, post mean(22,34,20,40)=29.0 → Δavg 9.0.
    //   Avg ≠ Weighted on every side → all 3 states (%+W, %+Avg, #) visibly exercised under start:fake.
    group_rollups: [
      {
        id: 'hospitality',
        is_scoring: false,
        benchmark: { percent: 64.0, percent_weighted: 70.6, percent_avg: 60.3, count: 120, visits: 170 },
        post: { percent: 72.5, percent_weighted: 76.9, percent_avg: 69.0, count: 277, visits: 360 },
        delta_pp: 8.5,
        delta_pp_weighted: 6.3,
        delta_pp_avg: 8.7,
        row_count: 3,
      },
      {
        id: 'upsell',
        is_scoring: true,
        benchmark: { percent: 20.0, percent_weighted: 18.3, percent_avg: 20.0, count: 42, visits: 230 },
        post: { percent: 29.5, percent_weighted: 30.1, percent_avg: 29.0, count: 166, visits: 552 },
        delta_pp: 9.5,
        delta_pp_weighted: 11.8,
        delta_pp_avg: 9.0,
        row_count: 4,
      },
    ],
  };
}

/**
 * Fetch the benchmark↔post comparison for one server. Windows are backend-defined
 * (PERIOD CONFIG) — no params. In fake mode returns deterministic canned data in
 * the real contract shape (happy path + the no-post-data edge row).
 */
export async function getServerComparison(
  tenantId: string,
  serverId: string,
): Promise<ServerComparisonResponse> {
  if (isFake()) {
    if (tenantId === FAKE_MR_TENANT_ID) return fakeMrComparisonResponse(serverId);
    return withOneFakeGroup(fakeComparisonResponse(serverId));
  }
  return api<ServerComparisonResponse>(
    `/api/server-scorecard/${encodeURIComponent(tenantId)}/${encodeURIComponent(serverId)}/comparison`,
  );
}


// ════════════════════════════════════════════════════════════════════════════
// feat-317 — per-role `groups` in fake mode (ADR §7.6).
//
// Every single-role fake response carries a ONE-element `groups` equal to its
// top-level fields, so fake-mode screens are unchanged. `fake-mr` is the
// two-role tenant: Greeter (with a drink column) and UCSF / Haas (none), with
// different metric columns, already in display-name order. React renders the
// groups verbatim and never re-sorts them.
// ════════════════════════════════════════════════════════════════════════════

const FAKE_SINGLE_ROLE_HEADER: GroupHeader = {
  role: 'server',
  display_name: 'Server',
  prompt_version_id: 'pv-active',
};

/** Append the T0-consistent one-element `groups` to a single-role fake response. */
function withOneFakeGroup<R extends object>(resp: R): R & { groups: Array<GroupHeader & R> } {
  return { ...resp, groups: [{ ...FAKE_SINGLE_ROLE_HEADER, ...resp }] };
}

/** T0: the legacy top-level fields mirror groups[0]. */
function mirrorFirstGroup<G extends GroupHeader>(groups: G[]): Omit<G, keyof GroupHeader> & { groups: G[] } {
  const { role: _r, display_name: _d, prompt_version_id: _p, ...first } = groups[0];
  return { ...first, groups } as Omit<G, keyof GroupHeader> & { groups: G[] };
}

const FAKE_MR_GREETER: GroupHeader = { role: 'greeter', display_name: 'Greeter', prompt_version_id: 'pv-g3' };
const FAKE_MR_UC: GroupHeader = { role: 'uc', display_name: 'UCSF / Haas', prompt_version_id: 'pv-uc2' };

const FAKE_MR_GREETER_COLUMNS: MetricColumn[] = [
  { key: 'garnish_g_greeting', label: 'Greeting' },
  { key: 'soup_upsell', label: 'Soup Upsell' },
];
const FAKE_MR_UC_COLUMNS: MetricColumn[] = [
  { key: 'breakfast_coffee_upsell', label: 'Coffee Upsell' },
  { key: 'soup_upsell', label: 'Soup Upsell' },
  { key: 'lunch_addon_upsell', label: 'Lunch Add-on' },
];

// ---- leaderboard (§6.2) ----

function fakeMrScorecardRow(
  server_id: string, name: string, initials: string,
  score: number | null, greetings: number | null, visits: number | null,
  totals: Record<string, number | null>, percents: Record<string, number | null>,
  drink: number | null, drinkPct: number | null,
): ServerScorecardRow {
  return {
    server_id, name, initials,
    location_id: 'loc-fake', location_name: FAKE_LOCATION,
    score, score_display: score === null ? '—' : `${score}%`,
    score_tier: null, score_band: null,
    greetings, visit_count: visits,
    metric_totals: totals, metric_percents: percents,
    drink_total: drink, drink_percent: drinkPct,
  };
}

function fakeMrScorecardResponse(): ServerScorecardResponse {
  const scale = FAKE_SCORE_SCALES[FAKE_MR_TENANT_ID];
  const common = {
    score_windowing: 'windowed',
    score_multiplier: scale.multiplier,
    score_scale_anchor_display: scale.anchorDisplay,
  };
  const groups: ServerScorecardGroup[] = [
    {
      ...FAKE_MR_GREETER,
      metric_columns: FAKE_MR_GREETER_COLUMNS,
      drink_column: { enabled: true, label: 'Wildwonder' },
      rows: [
        // Beatriz worked BOTH roles — she appears in both grids, each with that role's score (D8).
        fakeMrScorecardRow('s-beatriz', 'Beatriz Sandoval', 'BS', 22, 60, 58,
          { garnish_g_greeting: 55, soup_upsell: 8 }, { garnish_g_greeting: 94.8, soup_upsell: 13.8 }, 5, 8.6),
        fakeMrScorecardRow('s-emma', 'Emma Rocelis Amaya Perez de la Cruz', 'EC', 18, 40, 39,
          { garnish_g_greeting: 36, soup_upsell: 4 }, { garnish_g_greeting: 92.3, soup_upsell: 10.3 }, 3, 7.7),
        fakeMrScorecardRow('s-gerald', 'Gerald Oliver', 'GO', 0, 20, 19,
          { garnish_g_greeting: 17, soup_upsell: 0 }, { garnish_g_greeting: 89.5, soup_upsell: 0.0 }, 0, 0.0),
      ],
      ...common,
    },
    {
      ...FAKE_MR_UC,
      metric_columns: FAKE_MR_UC_COLUMNS,
      drink_column: { enabled: false, label: null },
      rows: [
        fakeMrScorecardRow('s-beatriz', 'Beatriz Sandoval', 'BS', 31, 24, 23,
          { breakfast_coffee_upsell: 4, soup_upsell: 2, lunch_addon_upsell: 1 },
          { breakfast_coffee_upsell: 17.4, soup_upsell: 8.7, lunch_addon_upsell: 4.3 }, null, null),
        fakeMrScorecardRow('s-hanns', 'Hanns Chiu', 'HC', 12, 34, 33,
          { breakfast_coffee_upsell: 2, soup_upsell: 1, lunch_addon_upsell: 1 },
          { breakfast_coffee_upsell: 6.1, soup_upsell: 3.0, lunch_addon_upsell: 3.0 }, null, null),
        // Roster server with no in-window shifts → one all-"—" row in their home role's group.
        fakeMrScorecardRow('s-denisse', 'Denisse Munoz', 'DM', null, null, null,
          { breakfast_coffee_upsell: null, soup_upsell: null, lunch_addon_upsell: null },
          { breakfast_coffee_upsell: null, soup_upsell: null, lunch_addon_upsell: null }, null, null),
      ],
      ...common,
    },
  ];
  return mirrorFirstGroup(groups) as ServerScorecardResponse;
}

// ---- Recent Shifts (§6.3) ----

const FAKE_MR_GREETER_SHIFT_COLUMNS: RecentShiftColumn[] = [
  { key: 'garnish_g_greeting', label: 'Greeting', display_order: 1, is_scoring: false },
  { key: 'soup_upsell', label: 'Soup Upsell', display_order: 2, is_scoring: true },
];
const FAKE_MR_UC_SHIFT_COLUMNS: RecentShiftColumn[] = [
  { key: 'breakfast_coffee_upsell', label: 'Coffee Upsell', display_order: 1, is_scoring: true },
  { key: 'soup_upsell', label: 'Soup Upsell', display_order: 2, is_scoring: true },
  { key: 'lunch_addon_upsell', label: 'Lunch Add-on', display_order: 3, is_scoring: true },
];
const FAKE_MR_DRINK_SHIFT_COLUMN: RecentShiftColumn = {
  key: 'drink_promotion', label: 'Wildwonder', display_order: 9, is_scoring: true,
};

function fakeMrShiftRow(
  shift_id: string, shift_date: string, shift_label: string, version: string,
  attempts: number, greetings: number, visits: number,
  metrics: Record<string, RecentShiftMetricValue>, drink: RecentShiftMetricValue | null,
  scoring: ScoreTooltipMetric[],
): RecentShiftRow {
  const score = visits > 0 ? Math.round((attempts / visits) * 100) : 0;
  return {
    shift_id, shift_date, shift_label,
    score_status: 'scored', score, score_display: `${score}%`, scored: true,
    prompt_version_id: version, greetings, visit_count: visits,
    metrics, drink,
    score_tooltip: fakeScoredTooltip(scoring, attempts, greetings, visits, 1, score, version),
  };
}

const mv = (percent: number | null, count: number | null): RecentShiftMetricValue =>
  ({ percent, count, present: percent !== null });

function fakeMrServerShiftsResponse(serverId: string, name: string): ServerShiftsResponse {
  const groups: ServerShiftsGroup[] = [
    {
      ...FAKE_MR_GREETER, server_id: serverId, name,
      metric_columns: FAKE_MR_GREETER_SHIFT_COLUMNS,
      drink_column: FAKE_MR_DRINK_SHIFT_COLUMN,
      rows: [
        fakeMrShiftRow('mr-g1', '2026-06-20', 'Greeter', 'pv-g3', 1, 6, 5,
          { garnish_g_greeting: mv(100.0, 5), soup_upsell: mv(20.0, 1) }, mv(20.0, 1),
          [{ label: 'Soup Upsell', count: 1, percent: 20.0 }]),
        fakeMrShiftRow('mr-g2', '2026-06-14', 'Greeter', 'pv-g3', 0, 4, 4,
          { garnish_g_greeting: mv(75.0, 3), soup_upsell: mv(0.0, 0) }, mv(0.0, 0), []),
      ],
    },
    {
      ...FAKE_MR_UC, server_id: serverId, name,
      metric_columns: FAKE_MR_UC_SHIFT_COLUMNS,
      drink_column: null,
      rows: [
        fakeMrShiftRow('mr-u1', '2026-06-18', 'UCSF / Haas', 'pv-uc2', 3, 8, 8,
          { breakfast_coffee_upsell: mv(25.0, 2), soup_upsell: mv(12.5, 1), lunch_addon_upsell: mv(0.0, 0) }, null,
          [{ label: 'Coffee Upsell', count: 2, percent: 25.0 }, { label: 'Soup Upsell', count: 1, percent: 12.5 }]),
        // Interim-period shift (D5): frozen snapshot has none of the UC keys → every cell "—".
        fakeMrShiftRow('mr-u2', '2026-06-09', 'UCSF / Haas', 'pv-g2', 0, 5, 5,
          { breakfast_coffee_upsell: mv(null, null), soup_upsell: mv(null, null), lunch_addon_upsell: mv(null, null) },
          null, []),
      ],
    },
  ];
  return mirrorFirstGroup(groups) as ServerShiftsResponse;
}

// ---- Manager / Store tables (§6.4) ----

function fakeMrStoreShiftsResponse(params: StoreShiftsParams): StoreShiftsResponse {
  const greeterMetrics = (g: number | null, s: number | null) => ({
    garnish_g_greeting: mv(g === null ? null : 100.0, g),
    soup_upsell: mv(s === null ? null : 25.0, s),
  });
  const all: StoreShiftsGroup[] = [
    {
      ...FAKE_MR_GREETER,
      metric_columns: FAKE_MR_GREETER_COLUMNS,
      drink_column: { key: 'drink_promotion', label: 'Wildwonder' },
      rows: [
        fakeStoreShiftRow({
          shift_id: 'mr-s1', server_name: 'Beatriz Sandoval', server_id: 's-beatriz',
          manager_shift_id: 'mgr-shift-1', score: 25, score_display: '25',
          greetings: 4, visit_count: 4, metrics: greeterMetrics(4, 1), drink: mv(25.0, 1),
        }),
        fakeStoreShiftRow({
          shift_id: 'mr-s2', server_name: 'Gerald Oliver', server_id: 's-gerald',
          manager_shift_id: 'mgr-shift-1', score: 0, score_display: '0',
          greetings: 3, visit_count: 3, metrics: greeterMetrics(3, 0), drink: mv(0.0, 0),
        }),
      ],
    },
    {
      ...FAKE_MR_UC,
      metric_columns: FAKE_MR_UC_COLUMNS,
      drink_column: null,
      rows: [
        // Same manager shift as the Greeter rows: Beatriz switched roles mid-day.
        fakeStoreShiftRow({
          shift_id: 'mr-s3', server_name: 'Beatriz Sandoval', server_id: 's-beatriz',
          manager_shift_id: 'mgr-shift-1', score: 38, score_display: '38',
          greetings: 8, visit_count: 8,
          metrics: {
            breakfast_coffee_upsell: mv(25.0, 2), soup_upsell: mv(12.5, 1), lunch_addon_upsell: mv(0.0, 0),
          },
          drink: null,
        }),
        // A different manager's shift — ManagerScorecard's manager filter empties this group for mgr-1.
        fakeStoreShiftRow({
          shift_id: 'mr-s4', server_name: 'Hanns Chiu', server_id: 's-hanns',
          manager_shift_id: 'mgr-shift-2', manager_id: 'mgr-2', manager_name: 'Luis Ortega',
          shift_date: '2026-06-11', score: 14, score_display: '14',
          greetings: 7, visit_count: 7,
          metrics: {
            breakfast_coffee_upsell: mv(14.3, 1), soup_upsell: mv(0.0, 0), lunch_addon_upsell: mv(null, null),
          },
          drink: null,
        }),
      ],
    },
  ];
  // Drill-in restriction first, then G1 (no group for a role left with no rows),
  // G2 (all empty → one anchor group with empty rows).
  const restricted = all.map((g) => ({
    ...g,
    rows: params.shiftId ? g.rows.filter((r) => r.manager_shift_id === params.shiftId) : g.rows,
  }));
  const kept = restricted.filter((g) => g.rows.length > 0);
  return mirrorFirstGroup(kept.length > 0 ? kept : [restricted[0]]) as StoreShiftsResponse;
}

// ---- Benchmark comparison (§6.5) ----

function fakeMrComparisonResponse(serverId: string): ServerComparisonResponse {
  // Greeter: today's happy-path fake (both windows populated, with a drink row).
  const greeter = fakeComparisonResponse(serverId);
  const ucColumns = FAKE_MR_UC_SHIFT_COLUMNS;
  const ucRow = (col: RecentShiftColumn, postPct: number, postVisits: number): ComparisonRow => ({
    key: col.key, label: col.label, is_scoring: col.is_scoring,
    benchmark: { count: 0, visits: 0, percent: null, percent_avg: null },
    post: fakeComparisonValue(postPct, postVisits, postPct),
    delta_pp: null, delta_pp_avg: null,
  });
  const groups: ServerComparisonGroup[] = [
    { ...FAKE_MR_GREETER, ...greeter },
    {
      // UC has post shifts but no benchmark shifts → today's State 3 (single window).
      ...FAKE_MR_UC,
      server_id: serverId,
      name: greeter.name,
      metric_columns: ucColumns,
      drink_column: null,
      windows: {
        benchmark: { start_date: greeter.windows.benchmark.start_date, end_date: greeter.windows.benchmark.end_date, shift_count: 0, visit_count: 0 },
        post: { start_date: greeter.windows.post.start_date, end_date: greeter.windows.post.end_date, shift_count: 6, visit_count: 150 },
      },
      comparisons: [ucRow(ucColumns[0], 14.0, 150), ucRow(ucColumns[1], 6.0, 150), ucRow(ucColumns[2], 3.3, 150)],
      drink_comparison: null,
      group_rollups: [{
        id: 'upsell', is_scoring: true,
        benchmark: { percent: null, percent_weighted: null, percent_avg: null, count: 0, visits: 0 },
        post: { percent: 7.8, percent_weighted: 7.8, percent_avg: 7.9, count: 35, visits: 450 },
        delta_pp: null, delta_pp_weighted: null, delta_pp_avg: null,
        row_count: 3,
      }],
    },
  ];
  return mirrorFirstGroup(groups) as ServerComparisonResponse;
}
